import App, { Container as NextContainer } from 'next/app';
import React from 'react';
import Helmet from 'react-helmet';
import ReactGA from 'react-ga';
import { ThemeProvider } from 'styled-components';
import { withRouter } from 'next/router';
import GlobalStyle from '../styles/global-style';
import { Router } from '../routes';
import theme from '../styles/theme';
import Header from '../components/header';
import Footer from '../components/footer';
import Auth from '../components/auth';
import PageTransition from '../components/page-transition';
import GdprRibbon from '../components/gdpr-ribbon';

ReactGA.initialize('UA-140090575-1');

class MyApp extends App {

  static async getInitialProps({ Component, ctx }) {
    const fromServer = !!ctx.req;

    const pageProps = {
      ...(Component.getInitialProps && await Component.getInitialProps(ctx)),
      fromServer,
    };

    return { pageProps };
  }

  state = {
    playHomeVideo: true,
    navOpen: false,
    popOutNavOpen: false,
    routingPageOffset: 0,
    showGDPR: false,
    transitionSpeed: 450,
  }

  componentDidMount() {
    Router.events.on('routeChangeStart', this.pageChange);

    ReactGA.pageview(this.props.router.asPath);
    this.initialGDPR();

    window.history.scrollRestoration = 'manual';

    Router.beforePopState(() => {
      setTimeout(() => { window.scrollTo(0, 0); }, 300);
      return true;
    });
  }

  componentDidUpdate() {
    const prevPage = this.state.prevPage && this.state.prevPage.page;

    if (prevPage !== this.props.router.asPath) {
      ReactGA.pageview(this.props.router.asPath);
      this.hasBeenHome(prevPage);
    }
  }

  hasBeenHome = (prevPage) => {
    if (prevPage === '/' && this.state.playHomeVideo === true) {
      this.setState({
        playHomeVideo: false,
      });
    }
  }

  initialGDPR = () => {
    const token = localStorage.getItem('w4w_gdpr');

    if (!token) {
      this.setState({ showGDPR: true });
    }
  }

  acceptGDPR = () => {
    this.setState({ showGDPR: false });
    localStorage.setItem('w4w_gdpr', true);
  }

  getTransitionSpeed = (url) => {
    const invalid = ['/account', '/payment', '/edit'];

    if (
      invalid.indexOf(url) >= 0 ||
      url.match(/\/payment\/[0-9]/) ||
      url.match(/\/edit/)
    ) return 0;
    return 450;
  }

  pageChange = (url) => {
    const { asPath } = this.props.router;
    const showPrev = ['/programs', '/fundraisers', '/couriers'].indexOf(this.transitionRoute()) > -1;

    this.setState((prevState) => {
      const prev = prevState.prevPage ? prevState.prevPage.filteredPage : '/';

      return {
        transitionSpeed: this.getTransitionSpeed(url),
        routingPageOffset: window.scrollY,
        prevPage: {
          filteredPage: showPrev ? prev : asPath,
          page: asPath,
        },
      };
    });

    if (this.state.navOpen) {
      this.toggleNav();
    }

    if (this.state.popOutNavOpen) {
      this.togglePopOutNav();
    }
  }

  toggleNav = () => {
    this.setState(prevState => ({
      navOpen: !prevState.navOpen,
    }));
  }

  togglePopOutNav = () => {
    this.setState(prevState => ({
      popOutNavOpen: !prevState.popOutNavOpen,
    }));
  }

  transitionRoute = () => {
    const { asPath } = this.props.router;

    const routeMatch = asPath.match(/(programs|couriers|fundraisers)(\/[\D].+)/);
    const regex = routeMatch && routeMatch[2] !== '/about' && new RegExp(routeMatch[2]);
    const fakeRoute = routeMatch ? asPath.replace(regex, '') : asPath;

    return fakeRoute;
  }

  render() {
    const { Component, pageProps, router } = this.props;
    const pageTransitionRoute = this.transitionRoute();
    const hideNav = ['/programs', '/fundraisers', '/couriers'].indexOf(pageTransitionRoute) > -1;

    return (
      <NextContainer>

        <style jsx>
          {`
              @font-face {
                font-family: "HK";
                src: url("${process.env.S3_ROOT}/w4w-static/fonts/hkgrotesk-regularlegacy.woff2") format("woff2"),
                    url("${process.env.S3_ROOT}/w4w-static/fonts/hkgrotesk-regularlegacy.woff") format("woff");
                font-weight: 400;
                font-display: swap;
              }

              @font-face {
                font-family: "HK";
                src: url("${process.env.S3_ROOT}/w4w-static/fonts/hkgrotesk-semiboldlegacy.woff2") format("woff2"),
                    url("${process.env.S3_ROOT}/w4w-static/fonts/hkgrotesk-semiboldlegacy.woff") format("woff");
                font-weight: 600;
                font-display: swap;
              }
            `}
        </style>

        <Helmet
          defaultTitle="Waves for Water"
          titleTemplate="%s | Waves for Water"
          meta={[
            { name: 'msapplication-TileColor', content: '#5f94a6' },
            { name: 'msapplication-config', content: `${process.env.S3_ROOT}/w4w-static/favicons/browserconfig.xml` },
            { name: 'theme-color', content: '#ffffff' },
          ]}
          link={[
            { rel: 'apple-touch-icon', sizes: '180x180', href: `${process.env.S3_ROOT}/w4w-static/favicons/apple-touch-icon.png` },
            { rel: 'apple-touch-icon', sizes: '180x180', href: `${process.env.S3_ROOT}/w4w-static/favicons/apple-touch-icon.png` },
            { rel: 'icon', type: 'image/png', sizes: '32x32', href: `${process.env.S3_ROOT}/w4w-static/favicons/favicon-32x32.png` },
            { rel: 'icon', type: 'image/png', sizes: '16x16', href: `${process.env.S3_ROOT}/w4w-static/favicons/favicon-16x16.png` },
            { rel: 'manifest', href: `${process.env.S3_ROOT}/w4w-static/favicons/site.webmanifest`, defer: true },
            { rel: 'mask-icon', href: `${process.env.S3_ROOT}/w4w-static/favicons/safari-pinned-tab.svg`, color: '#5bbad5' },
            { rel: 'shortcut icon', href: `${process.env.S3_ROOT}/w4w-static/favicons/favicon.ico` },
          ]}
        />

        <ThemeProvider theme={theme}>
          <Auth route={router.asPath}>
            {!hideNav &&
              <Header
                navOpen={this.state.navOpen}
                toggleNav={this.toggleNav}
                popOutNavOpen={this.state.popOutNavOpen}
                togglePopOutNav={this.togglePopOutNav}
              />
            }

            <GdprRibbon
              showGDPR={this.state.showGDPR}
              acceptGDPR={this.acceptGDPR}
            />
            <PageTransition speed={this.state.transitionSpeed} route={pageTransitionRoute}>
              <Component
                {...pageProps}
                prevPage={this.state.prevPage}
                playHomeVideo={this.state.playHomeVideo}
              />
            </PageTransition>
            <Footer />

            <GlobalStyle routingPageOffset={this.state.routingPageOffset} />
          </Auth>

        </ThemeProvider>

      </NextContainer>
    );
  }
}

export default withRouter(MyApp);
